import type { Dispatch, SetStateAction } from "react";

import { useReactiveVar } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { ChevronLeft, Share, ThreeDots } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Loading from "@/components/ui/spinner/Loading";
import { ROUTES } from "@/constants/routes";
import { qrTotalCountInfoVar } from "@/graphql/globalVar/state";
import useFolders from "@/pages/entryPage/hooks/useFolders";
import { localizedHref } from "@/utils/localizedHref";

import FolderOptionsSheet from "./FolderOptionsSheet";

interface IWebViewEntryInfoProps {
  setShowBurger: Dispatch<SetStateAction<boolean>>;
}

function WebViewEntryInfo({ setShowBurger }: IWebViewEntryInfoProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isShowFolderOptions, setIsShowFolderOptions] = useState(false);
  const { currentFolder, userFolders, onOpenFolder, loadingFolders } =
    useFolders();
  const qrTotalCountInfo = useReactiveVar(qrTotalCountInfoVar);

  useEffect(() => {
    setShowBurger(!currentFolder);
    return () => setShowBurger(true);
  }, [currentFolder, setShowBurger]);

  const handleGoBack = () => {
    if (currentFolder?.parentFolderId) {
      const parent = userFolders.find(
        (f) => f.id === currentFolder.parentFolderId
      );
      if (parent) {
        onOpenFolder(parent);
        return;
      }
    }
    navigate(localizedHref(ROUTES.ENTRY));
  };

  const qrCount = qrTotalCountInfo?.loading ? (
    <Loading />
  ) : (
    qrTotalCountInfo?.count
  );

  if (loadingFolders) return null;

  if (currentFolder)
    return (
      <>
        <div className="d-flex justify-content-between align-items-center gap-3 w-100">
          <Button
            variant="link"
            className="text-body p-0"
            onClick={handleGoBack}
          >
            <ChevronLeft size="24" />
          </Button>
          <div className="fs-6 fw-bold d-flex align-items-center gap-2 overflow-hidden">
            <div className="text-truncate">{currentFolder.name}</div>
            {currentFolder.isShared && <Share />}
            <div className="bg-primary-alt-subtle text-primary-alt rounded fs-8 py-1 px-2 flex-shrink-0">
              {qrCount}
            </div>
          </div>

          <Button
            variant="link"
            className="text-body p-0"
            onClick={() => setIsShowFolderOptions(true)}
          >
            <ThreeDots size="24" />
          </Button>
        </div>

        <FolderOptionsSheet
          folder={currentFolder}
          show={isShowFolderOptions}
          onHide={() => setIsShowFolderOptions(false)}
        />
      </>
    );

  return (
    <>
      <div className="fs-6 fw-bold d-flex align-items-center gap-2">
        <div>{t("page.myQrCodes.title")}</div>
        <span className="bg-primary-alt-subtle text-primary-alt rounded fs-8 py-1 px-2">
          {qrCount}
        </span>
      </div>
      <div className="flex-grow-1" />
    </>
  );
}

export default WebViewEntryInfo;
